import React, { useEffect } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import queryString from 'query-string';
import { Provider } from 'react-redux';
import { unstable_HistoryRouter as HistoryRouter, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import logger from 'shared/lib/logger';
import { loadPersistedState } from 'shared/lib/persistedState';
import { GA_USERPORTAL, I18N_LANGUAGES } from 'shared/constants';
import createStore, { history } from 'userportal/store';
import { actions as globalActions } from 'userportal/ducks/global';
import { actions as authActions } from 'userportal/ducks/auth';
import apiClient from 'userportal/lib/ApiClient';
import { preloadUserChunk } from 'userportal/lib/preloadUserChunk';
import App from 'userportal/components/App';
import i18n from 'shared/i18n/init';
import 'shared/scss/main.scss';
import config from './config';

// import * as serviceWorker from './serviceWorker';

// silent defaultProps error in console
const originalError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes('defaultProps')) return;
  originalError(...args);
};

i18n.on('initialized', () => {
  apiClient.setLanguage(i18n.language);
  i18n.changeLanguage(i18n.language);
});

window.dataLayer = window.dataLayer || [];
function gtag() {
  window.dataLayer.push(arguments);
}
gtag('consent', 'default', {
  ad_storage: 'denied',
  analytics_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied'
});
gtag('js', new Date());
gtag('config', GA_USERPORTAL);
window.gtag = gtag;

if (!config.developmentMode) {
  const setup = {
    dsn: 'https://d61b1bd8d1d6471680e2624be162a140@o402007.ingest.sentry.io/5262413',
    release: import.meta.env.REACT_APP_RELEASE_VERSION || undefined,
    environment: 'production',
    enableLogs: true,
    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      Sentry.reactRouterV6BrowserTracingIntegration({ useEffect, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes }),
      // Capture browser console output as Sentry logs (requires enableLogs).
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^\//, /^https:\/\/www\.skilleto\.cz\/_api/],
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  };

  Sentry.init(setup);
  logger('Sentry', setup);
}

const getLocation = (href) => {
  try {
    const l = document.createElement('a');
    l.href = href;
    return l;
  } catch {
    return {};
  }
};

const referrer = getLocation(document.referrer).hostname;
if (!localStorage.getItem('referrer') && !referrer.includes('skilleto')) {
  localStorage.setItem('referrer', referrer);
}

const initialState = {};

const persistedState = loadPersistedState();
if (persistedState !== null && persistedState.auth) {
  initialState.auth = persistedState.auth;
  apiClient.setToken(persistedState.auth.token);
  apiClient.setLanguage(persistedState.auth.language);
  apiClient.setCurrency(persistedState.auth.user.currency);

  if (persistedState.visited) {
    initialState.visited = persistedState.visited;
  }
}

const store = createStore(initialState);
if (initialState.auth && initialState.auth.token) {
  store.dispatch(authActions.authRestored(initialState.auth));
  preloadUserChunk();
}

// force language from query param "lng" (i18n switches by default)
const { lng } = queryString.parse(window.location.search);
if (lng && I18N_LANGUAGES.includes(lng)) {
  store.dispatch(authActions.setLanguage(lng));
}

if (config.developmentMode) {
  window['store'] = store;
}

apiClient.addErrorCallback((error = {}) => {
  if (error.status === 401) {
    store.dispatch(authActions.forceLogout());
  }
});

if (initialState.auth && initialState.auth.token) {
  store.dispatch(globalActions.bulkLoad());
} else {
  store.dispatch(globalActions.dialsLoad());
}

const rootElement = document.getElementById('root');
const app = (
  <Provider store={store}>
    <HistoryRouter history={history}>
      <App />
    </HistoryRouter>
  </Provider>
);

if (rootElement.querySelector('#navigation')) {
  hydrateRoot(rootElement, app, {
    onRecoverableError: (error, errorInfo) => {
      console.error('Hydration mismatch:', error, errorInfo?.componentStack);
    }
  });
} else {
  createRoot(rootElement).render(app);
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA

// serviceWorker.unregister();
